import { Box, Divider, Paper, Typography } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridActionsCellItem,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import { DateRange } from "@mui/x-date-pickers-pro";
import moment, { Moment } from "moment";
import { MutableRefObject, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  currencyFormatter,
  handleArrowNavigationForNumberCells,
} from "../../constants";
import { AppConfig } from "../../environement";
import {
  summarizeEarnings,
  withinDateRange,
} from "../../helpers/talentEarnings";
import { useJobs } from "../../redux/job/jobHooks";
import { DateRangePicker } from "../dateRangePicker/dateRangePicker";
import { useAppSelector } from "../hooks";
import {
  getterDivider,
  setterDivider,
} from "../jobCategory/jobCategoryRates/dataGridColDef";
import { selectedTalentID } from "../../redux/filmsReducer";

/**
 *
 * @returns {ReactElement} TalentJobs page
 */
export function TalentJobs({
  withDashboard = true,
}: {
  withDashboard?: boolean;
}) {
  const talentID = useAppSelector(selectedTalentID);
  const { jobs } = useJobs({
    filters: JSON.stringify([
      {
        name: "jobs.talentID",
        value: talentID,
        comparison: "eq",
      },
    ]),
  });
  const apiRef = useGridApiRef();
  const [date, setDate] = useState<DateRange<Moment>>([
    moment().subtract(7, "day"),
    moment(),
  ]);

  const jobsForRange = useCallback(() => {
    return jobs?.filter((t) => withinDateRange(t.createdAt, date)) ?? [];
  }, [jobs, date]);

  // Shared with the Virtual Ledger's cards, which show the same three figures
  // and have to match this tab to the cent.
  const { total, totalHours, avgHourlyRate } = summarizeEarnings(
    jobsForRange()
  );
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        flex: 1,
        minHeight: 0,
      }}
    >
      <DateRangePicker date={date} onChange={(e) => setDate(e)} />
      <Box sx={{ display: "flex", gap: 1 }}>
        <Paper sx={{ p: 1 }}>
          <Typography variant="caption">Total</Typography>
          <Typography variant="h6">
            <b>{currencyFormatter.format(total)}</b>
          </Typography>
        </Paper>
        <Paper sx={{ p: 1 }}>
          <Typography variant="caption">Total Hours</Typography>
          <Typography variant="h6">
            <b>{totalHours}</b>
          </Typography>
        </Paper>
        <Paper sx={{ p: 1 }}>
          <Typography variant="caption">Avg Hourly Rate</Typography>
          <Typography variant="h6">
            <b>{currencyFormatter.format(avgHourlyRate)}</b>
          </Typography>
        </Paper>
      </Box>
      <DataGridPro
        sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}
        rows={jobsForRange()}
        apiRef={apiRef}
        onCellKeyDown={handleArrowNavigationForNumberCells}
        density="compact"
        showToolbar
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          footer: { apiRef },
        }}
        columns={[
          { field: "projectName", headerName: "Project", width: 200 },
          {
            field: "createdAt",
            headerName: "Date",
            width: 200,
            type: "date",
            valueFormatter: (valueOrParams: any) => {
              const value = valueOrParams?.value ?? valueOrParams;
              return moment(value).format("MM/DD/YYYY");
            },
          },
          {
            field: "hourlyCalc",
            headerAlign: "center",
            headerName: "Hourly Rate",
            sortable: false,
            editable: true,
            type: "number",
            valueGetter: getterDivider("hourlyCalc", 100),
            valueSetter: setterDivider("hourlyCalc", 100),
            valueFormatter: (valueOrParams: any) => {
              const value = valueOrParams?.value ?? valueOrParams;
              return `${currencyFormatter.format(value)}/h`;
            },
          },

          {
            field: "hours",
            headerAlign: "center",
            align: "center",
            headerName: "Hours",
            sortable: false,
            editable: true,
            type: "number",
            valueGetter: getterDivider("hours", 100),
          },
          {
            field: "paymentDate",
            headerName: "Payment Date",
            flex: 1,
            type: "date",
            valueFormatter: (valueOrParams: any) => {
              const value = valueOrParams?.value ?? valueOrParams;
              return value !== "0001-01-01T00:00:00Z"
                ? moment(value).format("MM/DD/YYYY")
                : "No paid yet";
            },
          },
          {
            field: "totalAdjusted",
            align: "right",
            headerAlign: "center",
            headerName: "Total",
            editable: false,
            sortable: false,
            width: 120,
            type: "number",
            valueGetter: (_value: any, row: any) => row.total / 10000,
            valueSetter: setterDivider("totalAdjusted", 10000),
            valueFormatter: (valueOrParams: any) => {
              const value = valueOrParams?.value ?? valueOrParams;
              return `${currencyFormatter.format(value)}`;
            },
          },
          {
            field: "actions",
            type: "actions",
            sortable: false,
            headerName: "Actions",
            width: 100,
            cellClassName: "actions",
            getActions: (p) => {
              const actions = [];

              if (p.row.talentID) {
                actions.push(
                  <GridActionsCellItem
                    key={"vtransactions"}
                    icon={<i className="fa-solid fa-file-pdf"></i>}
                    label="Download PDF"
                    onClick={() => {
                      window.open(
                        `${AppConfig.api.endpoint}/films/jobs/${p.row.id}/invoice`,
                        "_blank"
                      );
                    }}
                  />
                );
              }

              return actions;
            },
          },
        ]}
      />
    </Box>
  );
}

function CustomToolbar() {
  return (
    <Toolbar>
      <Box sx={{ display: "flex", p: 1, width: "100%" }}>
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
      </Box>
    </Toolbar>
  );
}

function CustomFooter({ apiRef }: { apiRef: MutableRefObject<GridApiPro> }) {
  // const apiRef = useGridApiContext();
  const rows = apiRef.current?.getVisibleRowModels();

  let total = 0;
  rows.forEach((r) => (total += r.total ?? 0));

  return (
    <>
      <Divider />
      <Box
        sx={{ display: "flex", gap: 1, justifyContent: "end", p: 1, pr: 13 }}
      >
        Total: <b>{currencyFormatter.format(total / 10000)}</b>
      </Box>
    </>
  );
}
